import React, { useEffect, useState } from "react";
import { connect, useSelector } from "react-redux";
import config from "appConfig";
import { Link, useHistory } from "react-router-dom";
import { Checkbox, Table, Button, Modal, Tooltip, Icon } from "antd";
import styles from "./manageUserPage.scss";
import { UserSearchForm } from "../UserSearchForm";
import { IndexPagination } from "../../../components/IndexPagination";
import {
  getUsers,
  selectUsers,
  selectBlockReasons,
  blockUser,
  unblockUser,
  getBlockReasons,
  deleteUser,
  autoRenewal,
  toggleHideAllReducedPrices,
  toggleTwoFactor,
  triggerShadowBlock,
} from "./user.ducks";
import PageTitle from "../PageTitle";
import { checkEmptyObject, formatDate } from "../../../services";
import { selectUserInfo } from "../../Auth/auth.ducks";
import { UserRole } from "../../../models";
import { CreditsForm } from "./CreditsForm";
import { addCredits, getCreditsLog, selectCreditsLog } from "./credit.ducks";
import { AdminNotes } from "../AdminNotes";
import { selectModules, selectCategories } from "../selectors";
import CreateMemberPopup from "./CreateMemberPopup";
import { api, notification } from "../../../services";
import { ReasonPickerModal } from "../ReasonPickerModal";

const USER_PER_PAGE = 100;

const ManageUserPage = (props) => {
  const { users, creditsLog } = props;
  const history = useHistory();
  const modules = useSelector(selectModules) || [];
  const categories = useSelector(selectCategories) || [];
  const blockReasons = useSelector(selectBlockReasons) || [];

  const renderDominantCategories = (value) => {
    if (!Array.isArray(value) || value.length === 0) return "-";
    return value
      .map((entry) => {
        const m = modules.find(
          (x) => x.name === entry.module || x.route === entry.module
        );
        const c = categories.find(
          (x) =>
            (x.name === entry.category || x.route === entry.category) &&
            (x.moduleName === entry.module || x.moduleRoute === entry.module)
        );
        const mLabel = (m && (m.displayName || m.route || m.name)) || entry.module;
        const cLabel = (c && (c.displayName || c.route || c.name)) || entry.category;
        return [mLabel, cLabel].filter(Boolean).join(" / ");
      })
      .join(", ");
  };

  const [loading, setLoading] = useState(false);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState({});
  const [selectedUser, setSelectedUser] = useState({});
  const [creditsModalVisible, setCreditsModalVisible] = useState(false);
  const [adminNotesModalVisible, setAdminNotesModalVisible] = useState(false);
  const [createMemberVisible, setCreateMemberVisible] = useState(false);
  const [createMemberLoading, setCreateMemberLoading] = useState(false);
  const [blockModalVisible, setBlockModalVisible] = useState(false);
  const [blockingUserId, setBlockingUserId] = useState("");
  const [blockingUsername, setBlockingUsername] = useState("");
  const [blockLoading, setBlockLoading] = useState(false);

  const blockTooltip = <span>{"Blokiraj/Odblokiraj"}</span>;
  const shadowBlockTooltip = <span>Shadow blokiraj/odblokiraj</span>;
  const adminNotesTooltip = <span>{"Dodaj bilješku"}</span>;
  const adCreditsTooltip = <span>{"Dodaj kredite"}</span>;
  const autoRenewalTooltip = <span>{"Aktiviraj/Deaktiviraj automatsko obnavljanje oglasa"}</span>;
  const statisticsTooltip = <span>Statistika</span>;
  const tableText = { emptyText: "Nema podataka" };
  const hideReducedPriceTooltip = <span>{"Sakrij/Prikaži popust"}</span>
  const twoFactorTooltip = <span>{"Uključi/Isključi 2FA"}</span>

  const columns: any = [
    {
      title: "Id",
      dataIndex: "id",
      key: "id",
      class: "user-id",
      width: 100,
      render: (text, record) => (
        <div>
          {record.isBlocked && <div style={{ background: "red", padding: 3, color: "white", fontWeight: "bold" }}>
            Blokiran
          </div>}
          {record.chatShadowBannedAt && <div style={{ background: "red", padding: 3, color: "white", fontWeight: "bold" }}>
            Shadow Blokiran
          </div>}
          {text}
        </div>
      ),

    },
    {
      title: "Korisničko ime",
      dataIndex: "username",
      key: "username",
      width: 100,
      render: (text, record) => (
        <Link
          className="profile-link"
          title={text}
          to={`/admin/user/profile/${record.id}`}
        >
          {text}
        </Link>
      ),
    },
    {
      title: "Ime",
      dataIndex: "firstName",
      key: "firstName",
      width: 80,
    },
    {
      title: "Prezime",
      dataIndex: "lastName",
      key: "lastName",
      width: 80,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Mobitel",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      width: 80,
    },
    {
      title: "Registriran",
      dataIndex: "registrationDate",
      key: "registrationDate",
      width: 80,
      render: (value) => formatDate(value, config.fullTimeFormat),
    },
    {
      title: "Validiran",
      dataIndex: "isVerified",
      key: "isVerified",
      align: "center",
      width: 70,
      render: (value) => <Checkbox disabled defaultChecked={value} />,
    },
    {
      title: "Tip prodavača",
      dataIndex: "legalEntity",
      key: "legalEntity",
      width: 80,
      render: (value) => (
        <React.Fragment>
          {value
            ? "Pravna osoba"
            : "Fizička osoba"}
        </React.Fragment>
      ),
    },
    {
      title: "Krediti",
      dataIndex: "storeCreditBalance",
      key: "credits",
      align: "center",
      width: 70,
    },
    {
      title: "Dom. kategorija",
      dataIndex: "dominantCategories",
      key: "dominantCategories",
      width: 110,
      render: renderDominantCategories,
    },
    {
      title: "Akcije",
      key: "block",
      align: "center",
      dataIndex: "id",
      fixed: "right",
      width: 55,
      render: (value, record) => (
        <React.Fragment>
          <div
            style={{
              display: "inline-block",
              marginTop: "5px",
            }}
          >
            <Tooltip placement="topLeft" title={autoRenewalTooltip}>
              <Button
                type={record.autoRenewal ? "default" : "primary"}
                onClick={() => handleAutoRenewalClick(value, record)}
              // disabled={!record.eligibleForAutoRenewal} // Prop is not available in the current API response
              >
                <Icon type="retweet" className="manage-ad-icon" />
              </Button>
            </Tooltip>
          </div>
          <div
            style={{
              display: "inline-block",
              marginTop: "5px",
            }}
          >
            <Tooltip placement="topLeft" title={adCreditsTooltip}>
              <Button
                type="primary"
                onClick={() => handleAddCreditsClick(value, record)}
              >
                <Icon type="bank" className="manage-ad-icon" />
              </Button>
            </Tooltip>
          </div>
          <div
            style={{
              display: "inline-block",
              marginTop: "5px",
            }}
          >
            <Tooltip placement="topLeft" title={blockTooltip}>
              <Button
                type="primary"
                onClick={() => handleBlockClick(record.isBlocked, record)}
              >
                {record.isBlocked ? (
                  <Icon type="unlock" className="manage-ad-icon" />
                ) : (
                  <Icon type="lock" className="manage-ad-icon" />
                )}
              </Button>
            </Tooltip>
          </div>

          <div
            style={{
              display: "inline-block",
              marginTop: "5px",
            }}
          >
            <Tooltip placement="topLeft" title={shadowBlockTooltip}>
              <Button
                type="primary"
                onClick={() => handleShadowBlockClick(record)}
              >
                {record.chatShadowBannedAt ? (
                  <Icon type="exclamation-circle" />
                ) : (
                  <Icon type="exclamation" />
                )}
              </Button>
            </Tooltip>
          </div>

          <div
            style={{
              display: "inline-block",
              marginTop: "5px",
            }}
          >
            <Tooltip placement="topLeft" title={adminNotesTooltip}>
              <Button
                type="default"
                onClick={() => handleAdminNotesClick(value, record)}
              >
                <Icon type="info-circle" className="manage-ad-icon" />
              </Button>
            </Tooltip>
          </div>

          <div
            style={{
              display: "inline-block",
              marginTop: "5px",
            }}
          >
            <Tooltip placement="topLeft" title={statisticsTooltip}>
              <Button
                type="default"
                onClick={() => handleStatisticsClick(value, record)}
              >
                <Icon type="bar-chart" className="manage-ad-icon" />
              </Button>
            </Tooltip>
          </div>

          <div
            style={{
              display: "inline-block",
              marginTop: "5px",
            }}
          >
            <Tooltip placement="topLeft" title={hideReducedPriceTooltip}>
              <Button
                onClick={() => handleHideAllReducedPrices(record)}
                type={record.hideReducedPrices ? "primary" : "default"}
              >
                <Icon type="percentage" className="ad-percentage-icon" />
              </Button>
            </Tooltip>
          </div>

          <div
            style={{
              display: "inline-block",
              marginTop: "5px",
            }}
          >
            <Tooltip placement="topLeft" title={twoFactorTooltip}>
              <Button
                onClick={() => handleToggleTwoFactorClick(record)}
                type={record.twoFactorEnabled ? "danger" : "default"}
              >
                <Icon type="safety-certificate" className="manage-ad-icon" />
              </Button>
            </Tooltip>
          </div>
        </React.Fragment>
      ),
    },
  ];

  useEffect(() => {
    if (!blockReasons.length) {
      props.getBlockReasons();
    }
  }, []);

  useEffect(() => {
    if (props.currentUser.role === UserRole.superAdmin) {
      columns.push({
        title: "Obriši",
        align: "center",
        render: (value, record) => (
          <Button
            type="primary"
            onClick={() => handleDeleteClick(value, record)}
          >
            {"Obriši"}
          </Button>
        ),
      });
    }
  }, []);

  const handleAdminNotesClick = (value, record) => {
    setSelectedUser(record);
    setAdminNotesModalVisible(true);
  };

  const handleAdminNotesClose = () => {
    setSelectedUser({});
    setAdminNotesModalVisible(false);
  };

  const handleStatisticsClick = (value, record) => {
    history.push(`/admin/user/statistics/${record.id}`);
  };

  const handleDeleteClick = (value, record) => {
    Modal.confirm({
      title: 'Jeste li sigurni da želite trajno izbrisati korisnika  "{0}" ? Svi će podaci ovog korisnika biti uklonjeni i taj se postupak ne može poništiti.'.replace(
        "{0}",
        record.username
      ),
      // @ts-ignore
      centered: true,
      iconType: "NaN",
      onOk: () => {
        props.deleteUser(record.id);
      },
    });
  };

  const handleBlockClick = (value, record) => {
    const {
      unblockUser: handleUnblockUser,
      users,
    } = props;

    // Unblock keeps the old plain confirm — the reason picker only applies
    // when moving a user *into* blocked state.
    if (record.isBlocked) {
      Modal.confirm({
        title: `Želite li odblokirati ${record.username}?`,
        // @ts-ignore
        centered: true,
        iconType: "NaN",
        onOk() {
          handleUnblockUser(record.id, users);
        },
      });
      return;
    }

    setBlockingUserId(record.id);
    setBlockingUsername(record.username);
    setBlockModalVisible(true);
  };

  const handleBlockCancel = () => {
    if (blockLoading) return;
    setBlockModalVisible(false);
    setBlockingUserId("");
    setBlockingUsername("");
  };

  const handleBlockConfirm = (reasonId, note, basedOn) => {
    if (!blockingUserId) return;
    const targetId = blockingUserId;
    setBlockLoading(true);
    props.blockUser(targetId, reasonId, note, basedOn, props.users, (success) => {
      setBlockLoading(false);
      if (!success) {
        notification.showError("Blokiranje korisnika nije uspjelo");
        return;
      }
      setBlockModalVisible(false);
      setBlockingUserId("");
      setBlockingUsername("");
    });
  };

  const handleShadowBlockClick = (record) => {

    Modal.confirm({
      title: record.chatShadowBannedAt
        ? `Jeste li sigurni da želite poništiti shadow block za ${record.username}?`
        : `Jeste li sigurni da želite shadow blokirati ${record.username}?`,
      // @ts-ignore
      centered: true,
      iconType: "NaN",
      onOk() {
        props.triggerShadowBlock(record.id, !Boolean(record.chatShadowBannedAt), (callback) => {
          if (callback) {
            setLoading(true)
            props.getUsers(searchCriteria, undefined, undefined, () => setLoading(false));
          }
        })
      }
    });
  };

  const handleAutoRenewalClick = (value, record) => {
    Modal.confirm({
      title: 'Želite li {0} automatsko obnavljanje oglasa za korisnika "{1}" ?'
        .replace(
          "{0}",
          value
            ? "deaktivirati"
            : "aktivirati"
        )
        .replace("{1}", record.username),
      // @ts-ignore
      centered: true,
      iconType: "NaN",
      onOk() {
        props.autoRenewal(!record.autoRenewal, record.id, (success) => {
          if (success) {
            props.getUsers(searchCriteria);
          }
        });
      },
    });
  };

  const handleAddCreditsClick = (value, record) => {
    setSelectedUser(record);
    props.getCreditsLog({
      email: record.email,
    });

    setCreditsModalVisible(true);
  };

  const handleCreditsModalSubmit = (values) => {
    setCreditsLoading(true);
    props.addCredits(selectedUser.id, values, (success) => {
      if (success) {
        props.getCreditsLog(
          {
            email: selectedUser.email,
          },
          () => setCreditsLoading(false)
        );

        props.getUsers(searchCriteria);
      }
    });
  };

  const handleCreditsModalClose = () => {
    setCreditsModalVisible(false);
    setSelectedUser({});
  };

  const handleCreditsPageChange = (page) => {
    setCreditsLoading(true);
    props.getCreditsLog(
      {
        email: selectedUser.email,
        page,
      },
      () => setCreditsLoading(false)
    );
  };

  const handleHideAllReducedPrices = (record) => {
    Modal.confirm({
      title: !record.hideReducedPrices
        ? "Želite li isključiti prikaz snižene cijene za sve oglase korisnika {0}?".replace("{0}", record.username)
        : "Želite li prikazati snižene cijene za sve oglase korisnika {0}?".replace("{0}", record.username),
      // @ts-ignore
      centered: true,
      iconType: "NaN",
      onOk() {
        props.toggleHideAllReducedPrices(
          record,
          (success) => {
            if (success) {
              props.getUsers(searchCriteria);
            }
          }
        );
      },
    });
  };

  const handleToggleTwoFactorClick = (record) => {
    Modal.confirm({
      title: 'Želite li {0} 2FA za korisnika "{1}"?'
        .replace(
          "{0}",
          record.twoFactorEnabled
            ? "isključiti"
            : "uključiti"
        )
        .replace("{1}", record.username),
      // @ts-ignore
      centered: true,
      iconType: "NaN",
      onOk() {
        props.toggleTwoFactor(
          record.id,
          !record.twoFactorEnabled
        );
      },
    });
  };

  const handleSearch = (searchCriteria) => {
    setSearchCriteria(searchCriteria);
    search(searchCriteria);
  };

  const handlePageChange = (page) => {
    search(searchCriteria, page);
  };

  function search(searchCriteria = {}, page = 1) {
    setLoading(true);

    props.getUsers(searchCriteria, page, USER_PER_PAGE, () => setLoading(false));
  }

  const handleCreateMember = (values, callback) => {
    setCreateMemberLoading(true);

    const handleError = async (response) => {
      try {
        const error = await response.json();
        notification.showError(error.message || "Greška pri kreiranju korisnika");
      } catch (e) {
        notification.showError("Greška pri kreiranju korisnika");
      }
      callback && callback(false);
      setCreateMemberLoading(false);
    };

    api.User.createMember(values, handleError)
      .then((result) => {
        if (!result) return;
        notification.show("Korisnik je uspješno kreiran");
        callback && callback(true);
        search(searchCriteria);
        setCreateMemberLoading(false);
      });
  };

  return (
    <React.Fragment>
      <PageTitle title={"Upravljaj korisnicima"} />
      {/*
      // @ts-ignore */}
      <UserSearchForm onSubmit={handleSearch} onCreateMember={() => setCreateMemberVisible(true)} />
      <div>
        <Table
          loading={loading}
          rowKey="username"
          bordered
          dataSource={users.data}
          columns={columns}
          pagination={false}
          locale={tableText}
          scroll={{ x: "100%" }}
          className={`admin-table ${styles.table}`}
        />
        {checkEmptyObject(users) ? (
          <IndexPagination
            total={users.count}
            page={users.currentPage}
            itemPerPage={USER_PER_PAGE}
            onPageChange={handlePageChange}
          />
        ) : null}
      </div>

      <CreditsForm
        show={creditsModalVisible}
        loading={creditsLoading}
        user={selectedUser}
        onSubmit={handleCreditsModalSubmit}
        onCancel={handleCreditsModalClose}
        onPageChange={handleCreditsPageChange}
        log={creditsLog}
      />

      <AdminNotes
        show={adminNotesModalVisible}
        entityId={selectedUser.id}
        entityName={selectedUser.username}
        onClose={handleAdminNotesClose}
      />

      <CreateMemberPopup
        isVisible={createMemberVisible}
        loading={createMemberLoading}
        onSubmit={handleCreateMember}
        onDismiss={() => setCreateMemberVisible(false)}
      />

      <ReasonPickerModal
        visible={blockModalVisible}
        title={
          blockingUsername
            ? `Blokiraj korisnika ${blockingUsername}`
            : "Blokiraj korisnika"
        }
        reasons={blockReasons}
        submitText="Blokiraj"
        loading={blockLoading}
        onCancel={handleBlockCancel}
        onConfirm={handleBlockConfirm}
      />
    </React.Fragment>
  );
};

ManageUserPage.defaultProps = {
  users: {},
};

export default connect(
  (state) => ({
    users: selectUsers(state),
    currentUser: selectUserInfo(state),
    creditsLog: selectCreditsLog(state),
  }),
  {
    getUsers,
    blockUser,
    unblockUser,
    getBlockReasons,
    deleteUser,
    getCreditsLog,
    addCredits,
    autoRenewal,
    toggleHideAllReducedPrices,
    toggleTwoFactor,
    triggerShadowBlock,
  }
)(ManageUserPage);



// WEBPACK FOOTER //
// ./src/routes/Admin/ManageUserPage/ManageUserPage.tsx